import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check } from "lucide-react";
import { useJourney } from "../context/JourneyContext";
import { ASSESSMENT_QUESTIONS, scoreAssessment, ASSESSMENT_RESULT_COPY } from "../lib/assessment";

export const JourneyAssessment = () => {
  const { assessmentOpen, closeAssessment, applyAssessment } = useJourney();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const total = ASSESSMENT_QUESTIONS.length;
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === total;

  const choose = (qid, stage) => setAnswers((a) => ({ ...a, [qid]: stage }));

  const reveal = () => {
    const res = scoreAssessment(Object.values(answers));
    setResult(res);
    applyAssessment(res.stage.n);
  };

  const close = () => {
    closeAssessment();
    setTimeout(() => {
      setAnswers({});
      setResult(null);
    }, 300);
  };

  const goToStage = () => {
    const to = result.stage.to;
    close();
    navigate(to);
  };

  return (
    <AnimatePresence>
      {assessmentOpen && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="assessment-modal"
        >
          <motion.div
            className="absolute inset-0 bg-navy-deep/85 backdrop-blur-sm"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-sm border border-gold/25 bg-navy shadow-2xl shadow-black/50"
          >
            <div className="grain sticky top-0 z-10 flex items-center justify-between border-b border-gold/15 bg-navy/95 px-8 py-5 backdrop-blur-xl">
              <div>
                <p className="overline">Comienza tu viaje</p>
                <p className="mt-1 font-serif text-2xl text-cream">Encuentra tu punto de partida</p>
              </div>
              <button onClick={close} className="text-cream/60 transition-colors hover:text-gold" data-testid="assessment-close" aria-label="Cerrar">
                <X size={22} />
              </button>
            </div>

            <div className="px-8 py-8">
              {!result ? (
                <>
                  <div className="flex flex-col gap-10">
                    {ASSESSMENT_QUESTIONS.map((q, qi) => (
                      <div key={q.id} data-testid={`assessment-q-${qi + 1}`}>
                        <p className="mb-4 flex items-baseline gap-3 font-serif text-2xl text-cream">
                          <span className="text-base text-gold/60">0{qi + 1}</span>
                          {q.prompt}
                        </p>
                        <div className="flex flex-col gap-2.5">
                          {q.options.map((opt, oi) => {
                            const selected = answers[q.id] === opt.stage;
                            return (
                              <button
                                key={opt.label}
                                onClick={() => choose(q.id, opt.stage)}
                                className={`flex items-center gap-3 rounded-sm border px-5 py-3.5 text-left text-sm font-light transition-colors ${
                                  selected
                                    ? "border-gold bg-gold/10 text-cream"
                                    : "border-gold/20 text-cream/70 hover:border-gold/50 hover:text-cream"
                                }`}
                                data-testid={`assessment-q${qi + 1}-opt${oi + 1}`}
                              >
                                <span
                                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                    selected ? "border-gold bg-gold text-navy" : "border-gold/40"
                                  }`}
                                >
                                  {selected && <Check size={12} />}
                                </span>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-10 flex flex-wrap items-center gap-5">
                    <button
                      onClick={reveal}
                      disabled={!allAnswered}
                      className="btn-gold disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="assessment-submit"
                    >
                      Ver mi recomendación <ArrowRight size={15} />
                    </button>
                    <span className="text-xs font-light text-cream/50">
                      {answeredCount} de {total} respondidas
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-start" data-testid="assessment-result">
                  <p className="overline mb-6 flex items-center gap-4">
                    <span className="h-px w-10 bg-gold" /> Tu punto de partida
                  </p>
                  <p className="max-w-lg text-base font-light leading-relaxed text-cream/75">
                    {ASSESSMENT_RESULT_COPY}
                  </p>

                  <div className="mt-8 flex w-full items-center gap-5 rounded-sm border border-gold/30 bg-gold/5 p-6">
                    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-gold bg-gold text-navy">
                      <result.stage.icon size={26} />
                    </span>
                    <div>
                      <span className="block text-[0.6rem] uppercase tracking-luxe text-gold/70">
                        Nivel 0{result.stage.n} · Etapa recomendada
                      </span>
                      <h3 className="font-serif text-3xl text-cream">{result.stage.title}</h3>
                      <p className="mt-2 max-w-md text-sm font-light leading-relaxed text-cream/70">
                        {result.stage.why}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    <button onClick={goToStage} className="btn-gold" data-testid="assessment-start">
                      Comenzar aquí <ArrowRight size={15} />
                    </button>
                    <button
                      onClick={() => {
                        setResult(null);
                      }}
                      className="text-sm text-cream/70 underline-offset-4 transition-colors hover:text-gold hover:underline"
                      data-testid="assessment-retry"
                    >
                      Volver a responder
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
